import { useCallback, useRef, useState } from "react";
import { FunctionsError } from "firebase/functions";
import { callable } from "../lib/firebase";

/**
 * Callable Cloud Function çağrısı — TEK hata kapısı.
 *
 * ESKİ SİSTEMİN HATASI (post.php): her durumda HTTP 200 ve
 * "Statü başarıyla güncellendi" dönüyordu; jQuery'nin .fail() bloğu hiç
 * tetiklenmiyordu. Kullanıcı kaydın gittiğini sanıyor, veri yazılmamış oluyordu.
 *
 * Yeni kural: HttpsError mesajları sunucuda Türkçe üretilir
 * (functions/src/core/errors.ts) ve kullanıcıya AYNEN gösterilir. Burada mesaj
 * yeniden yazılmaz, yumuşatılmaz, "başarılı" ile değiştirilmez.
 */

/** Ağ/altyapı kaynaklı, sunucudan Türkçe mesaj gelmeyen kodlar. */
const INFRA_MESSAGES: Record<string, string> = {
  unavailable: "Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edip tekrar deneyin.",
  "deadline-exceeded": "İşlem zaman aşımına uğradı. Kaydın durumunu kontrol edin.",
  cancelled: "İşlem iptal edildi.",
  unauthenticated: "Oturumunuz sona ermiş. Lütfen tekrar giriş yapın.",
  "resource-exhausted": "Sistem şu anda yoğun. Lütfen birkaç saniye sonra tekrar deneyin.",
};

/** FunctionsError'ı sınıf kimliğine güvenmeden tanır (çift paket kurulumuna karşı). */
function asFunctionsError(e: unknown): { code: string; message: string } | null {
  if (e instanceof FunctionsError) return { code: e.code, message: e.message };
  if (
    typeof e === "object" && e !== null &&
    typeof (e as { code?: unknown }).code === "string" &&
    (e as { code: string }).code.startsWith("functions/")
  ) {
    return {
      code: (e as { code: string }).code,
      message: String((e as { message?: unknown }).message ?? ""),
    };
  }
  return null;
}

/** Sunucu mesajı yoksa/anlamsızsa Türkçe karşılığa düşer, aksi hâlde AYNEN döner. */
export function callableErrorMessage(e: unknown): string {
  const fe = asFunctionsError(e);
  if (fe) {
    // e.code "functions/permission-denied" biçiminde gelir.
    const bare = fe.code.replace(/^functions\//, "");
    const msg = fe.message.trim();
    // firebase-functions, mesaj verilmediğinde message alanına kodu koyar.
    if (!msg || msg === bare || msg === fe.code) {
      return INFRA_MESSAGES[bare] ?? `İşlem başarısız (${bare}).`;
    }
    // INTERNAL dışındaki tüm mesajlar sunucuda Türkçe üretilir → aynen göster.
    return msg;
  }
  if (e instanceof Error && e.message) return e.message;
  return "Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.";
}

export interface CallableActionState<TIn, TOut> {
  /** Hata olursa `null` döner (ASLA fırlatmaz); mesaj `error` alanındadır. */
  run: (data: TIn) => Promise<TOut | null>;
  pending: boolean;
  /** `pending` ile aynı — diğer modüllerin kullandığı ad. */
  loading: boolean;
  error: string | null;
  result: TOut | null;
  reset: () => void;
}

export function useCallableAction<TIn, TOut = unknown>(
  name: string,
): CallableActionState<TIn, TOut> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TOut | null>(null);

  // httpsCallable referansı sabit kalsın (her render'da yeniden üretilmesin).
  const fnRef = useRef<((data: TIn) => Promise<TOut>) | null>(null);
  if (fnRef.current === null) fnRef.current = callable<TIn, TOut>(name);

  const run = useCallback(async (data: TIn): Promise<TOut | null> => {
    setPending(true);
    setError(null);
    try {
      const out = await fnRef.current!(data);
      setResult(out);
      return out;
    } catch (e) {
      const msg = callableErrorMessage(e);
      console.error(`Callable hatası: ${name}`, e);
      setError(msg);
      setResult(null);
      return null;
    } finally {
      setPending(false);
    }
  }, [name]);

  const reset = useCallback(() => {
    setError(null);
    setResult(null);
  }, []);

  return { run, pending, loading: pending, error, result, reset };
}
